import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import { LinkIcon, SparklesIcon } from '@heroicons/react/solid'

import { useDebouncedEffect } from '@/utils/useDebouncedEffect'
import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'
import classNames from '@/utils/classNamesLocal'
import Loading from '@/shared/Loading'
import PlayPauseButton from '@/shared/Buttons/PlayPauseButton'
import Pagination from '@/shared/Pagination'
import Card from '@/shared/Card'
import VisibilityControl, { VISIBILITY_OPTIONS } from '@/shared/VisibilityControl'
import PublicDemoRow from '@/pages/Users/PublicProfile/PublicDemoRow'
import NoSubmissions from '@/pages/Users/ProfileSubmissions/NoSubmissions'
import NoResults from '@/pages/Users/ProfileSubmissions/NoResults'

const PinIcon = ({ filled, className }) => (
  <svg viewBox='0 0 24 24' className={className} fill={filled ? 'currentColor' : 'none'} stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
    <line x1='12' x2='12' y1='17' y2='22' />
    <path d='M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z' />
  </svg>
)

const HELP = {
  public: 'Shows on your profile. Anyone can open it.',
  unlisted: 'Off your profile, but anyone with the link can open it.',
  private: 'Off your profile. Only you and the project owner can open it.'
}

const SubmissionRow = ({ submission, showcased, onShowcase, onUnshowcase }) => {
  const [currentUser] = useGlobalState('currentUser')
  const { putpostRequest } = useQuery()
  const [permissions, setPermissions] = useState(submission.permissions)

  const change = (perm) => {
    const previous = permissions
    setPermissions(perm)
    putpostRequest(`/api/v3/auditions/${submission.id}`, 'PATCH', { audition: { permissions: perm } }, (err) => { if (err) { setPermissions(previous) } })
  }

  return <li className='rounded-xl bg-white p-3 shadow-sm dark:bg-gray-700'>
    <div className='flex items-start gap-x-3'>
      <img className='h-12 w-12 flex-shrink-0 rounded-lg bg-gray-200 object-cover' src={submission.roleImageUrl} alt='' />
      <div className='min-w-0 flex-1'>
        <div className='text-xs uppercase text-gray-500 dark:text-gray-300'>{submission.createdAt}</div>
        <div className='truncate text-sm font-medium text-gray-900 dark:text-gray-100'>{submission.projectName}</div>
        <a href={`/projects/${submission.projectSlug}`} className='truncate text-sm font-semibold text-cccblue'>{submission.roleName}</a>
        { submission.remoteLink && <a href={submission.remoteLink} target='_blank' rel='noreferrer' className='mt-0.5 inline-flex items-center gap-x-1 truncate text-xs text-cccblue hover:text-cccblue-alt'><LinkIcon className='h-3.5 w-3.5' />{submission.remoteLink}</a> }
      </div>
      <div className='flex flex-shrink-0 items-center gap-x-2'>
        { submission.audioUrl && <PlayPauseButton mediaUrl={submission.audioUrl} objectId={submission.id} objectKind='Audition' /> }
        <button type='button' onClick={showcased ? onUnshowcase : onShowcase} aria-pressed={showcased}
          title={showcased ? 'Unpin from your profile' : currentUser.canShowcase ? 'Pin to the top of your profile' : 'Pinning is a Premium perk'}
          className={classNames('rounded-full p-1.5 focus:outline-none focus:ring-2 focus:ring-cccblue', showcased ? 'text-cccorange hover:bg-cccorange/10' : 'text-gray-400 hover:bg-gray-100 hover:text-cccorange dark:hover:bg-gray-600')}>
          <PinIcon filled={showcased} className='h-5 w-5' />
        </button>
      </div>
    </div>
    <div className='mt-2 flex flex-wrap items-center justify-between gap-2'>
      <VisibilityControl value={permissions} onChange={change} />
      <span className='text-xs text-gray-500 dark:text-gray-400'>{HELP[permissions]}</span>
    </div>
    { submission.note && <div className='mt-2 text-xs text-gray-700 dark:text-gray-300 sm:text-sm' dangerouslySetInnerHTML={{ __html: submission.note }} /> }
    { submission.demo?.id && <div className='mt-2'><PublicDemoRow demo={submission.demo} /></div> }
  </li>
}

const ProfileSubmissions = () => {
  const [currentUser] = useGlobalState('currentUser')
  const [, setToast] = useGlobalState('toast')
  const { getRequest, putpostRequest } = useQuery()
  const [submissions, setSubmissions] = useState([])
  const [showcaseIds, setShowcaseIds] = useState({})
  const [counts, setCounts] = useState({ public: 0, unlisted: 0, private: 0 })
  const [meta, setMeta] = useState({})
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [contains, setContains] = useState('')
  const [filter, setFilter] = useState('all')
  const [bulk, setBulk] = useState(null) // pending visibility for the confirm
  const [bulking, setBulking] = useState(false)
  const [upsell, setUpsell] = useState(false)
  const scrollRef = useRef(null)

  const fetchData = (resetPage = false) => {
    setLoading(true)
    const data = { contains, page: resetPage ? 1 : page }
    if (filter !== 'all') { data.permissions = filter }
    getRequest('/api/v3/auditions/profile_auditions', data, (err, jsonData) => {
      setLoading(false)
      if (err) { return }
      setSubmissions(jsonData.submissions)
      setCounts(jsonData.counts || counts)
      setMeta(jsonData.meta)
    })
  }
  const fetchShowcases = () => {
    getRequest('/api/v3/showcases', {}, (err, jsonData) => {
      if (err) { return }
      setShowcaseIds(Object.fromEntries((jsonData.showcases || []).map((s) => [s.auditionId, s.id])))
    })
  }

  useDebouncedEffect(() => { fetchData(true) }, 200, [contains, filter])
  useEffect(() => { fetchData() }, [page])
  useEffect(() => { fetchShowcases() }, [])

  const showcase = (submission) => {
    if (!currentUser.canShowcase) { setUpsell(true); setTimeout(() => setUpsell(false), 1500); return }
    putpostRequest('/api/v3/showcases', 'POST', { showcase: { audition_id: submission.id } }, (err) => { if (!err) { fetchShowcases() } })
  }
  const unshowcase = (submission) => {
    putpostRequest(`/api/v3/showcases/${showcaseIds[submission.id]}`, 'DELETE', {}, (err) => { if (!err) { fetchShowcases() } })
  }

  const runBulk = () => {
    setBulking(true)
    putpostRequest('/api/v3/auditions/bulk_permissions', 'POST', { permissions: bulk }, (err, jsonData) => {
      setBulking(false)
      setBulk(null)
      if (err) { return }
      setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
        <p className='text-sm font-medium text-green-600 dark:text-green-300'>Done</p>
        <p className='mt-1 text-sm text-green-500 dark:text-green-300'>{jsonData.changed} {jsonData.changed === 1 ? 'submission is' : 'submissions are'} now {jsonData.permissions}.</p>
      </div>)
      fetchData(true)
    })
  }

  const total = counts.public + counts.unlisted + counts.private

  return <div className='md:grid md:grid-cols-4 md:gap-6'>
    <div className='mb-4 md:mb-0'>
      <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>Submissions on your profile</h3>
      <p className='mt-1 text-sm text-gray-600 dark:text-gray-300'>Public ones show on your profile. Pin the ones you want at the top.</p>
      <p className='mt-3 text-sm text-gray-600 dark:text-gray-300'>Comments, listens, and awards are on <Link to='/submissions'>my submissions</Link>.</p>
    </div>
    <Card>
      <div className='flex flex-wrap items-center gap-2'>
        <input type='search' value={contains} onChange={(e) => setContains(e.target.value)} placeholder='Search by project or role' aria-label='Search submissions' className='!w-auto min-w-[14rem] flex-1' />
        <div role='group' aria-label='Filter by visibility' className='inline-flex rounded-full bg-gray-100 p-0.5 dark:bg-gray-700'>
          { [['all', 'All', total], ...VISIBILITY_OPTIONS.map((o) => [o.value, o.label, counts[o.value]])].map(([value, label, count]) => (
            <button key={value} type='button' onClick={() => setFilter(value)} aria-pressed={filter === value}
              className={classNames('rounded-full px-3 py-1 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-cccblue',
                filter === value ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-900 dark:text-white' : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white')}>
              {label} <span className='text-gray-400'>{count}</span>
            </button>
          ))}
        </div>
      </div>

      { total > 1 && <div className='flex flex-wrap items-center gap-2 rounded-lg bg-gray-50 p-2 text-xs dark:bg-gray-900'>
        <span className='font-semibold text-gray-700 dark:text-gray-200'>Set all {total} to</span>
        { !bulk && VISIBILITY_OPTIONS.map((o) => (
          <button key={o.value} type='button' onClick={() => setBulk(o.value)} className='rounded-full bg-white px-2.5 py-1 font-semibold text-gray-700 ring-1 ring-gray-200 hover:ring-cccblue hover:text-cccblue dark:bg-gray-700 dark:text-gray-100 dark:ring-gray-600'>{o.label}</button>
        ))}
        { bulk && <>
          <span className='text-gray-700 dark:text-gray-200'>{bulk}? {HELP[bulk]}</span>
          <button type='button' onClick={runBulk} disabled={bulking} className='rounded-full bg-cccblue px-3 py-1 font-semibold text-white hover:bg-cccblue-alt disabled:opacity-60'>{ bulking ? 'Working...' : `Yes, all ${total}` }</button>
          <button type='button' onClick={() => setBulk(null)} className='font-semibold text-gray-500 hover:text-gray-900 dark:hover:text-white'>Never mind</button>
        </> }
      </div> }

      { !currentUser.canShowcase && <div className={classNames('flex flex-col gap-3 rounded-xl border-2 p-4 transition-colors sm:flex-row sm:items-center', upsell ? 'border-cccpurple bg-cccpurple/15' : 'border-cccpurple/30 bg-cccpurple/5 dark:bg-cccpurple/10')}>
        <span className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-cccpurple text-white'><PinIcon filled className='h-5 w-5' /></span>
        <div className='min-w-0 flex-1'>
          <div className='font-semibold text-gray-900 dark:text-white'>Pin your best work to the top of your profile</div>
          <div className='text-sm text-gray-600 dark:text-gray-300'>Pinned submissions show first, before everything else. Any paid plan unlocks it.</div>
        </div>
        <Link to='/membership' className='inline-flex flex-shrink-0 items-center gap-x-1 rounded-full bg-cccpurple px-4 py-2 text-sm font-semibold text-white hover:bg-cccpurple-alt hover:text-white'>
          <SparklesIcon className='h-4 w-4' /> See the plans
        </Link>
      </div> }

      <div ref={scrollRef}>
        { loading && <Loading /> }
        { !loading && submissions.length === 0 && (contains || filter !== 'all' ? <NoResults /> : <NoSubmissions />) }
        <ul className='space-y-2'>
          { submissions.map((sub) => (
            <SubmissionRow key={`sub${sub.id}`} submission={sub} showcased={!!showcaseIds[sub.id]} onShowcase={() => showcase(sub)} onUnshowcase={() => unshowcase(sub)} />
          ))}
        </ul>
        { submissions.length > 0 && <div className='mt-2'><Pagination meta={meta} callback={setPage} scrollRef={scrollRef} /></div> }
      </div>
    </Card>
  </div>
}

export default ProfileSubmissions

SubmissionRow.propTypes = {
  submission: PropTypes.object.isRequired,
  showcased: PropTypes.bool,
  onShowcase: PropTypes.func.isRequired,
  onUnshowcase: PropTypes.func.isRequired
}
